/** @jsxImportSource @emotion/react */
import { Box, CircularProgress, Theme, Link as MaterialLink, Typography, useTheme } from '@mui/material';
import CommonPageLayout from 'components/layouts/CommonPageLayout';
import React, { useContext, useEffect } from 'react';
import { UseStyles } from 'types/styles';
import TemplatesItem from 'components/TemplatesItem/TemplatesItem';
import { useTemplatesPage } from 'pages/TemplatesPages/useTemplatesPage';
import { FormattedMessage, useIntl } from 'react-intl';
import Heading from 'components/Heading';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import TabGroupIcon from 'components/icons/TabGroupIcon';
import Proposals from 'components/Proposals';
import { usePaths } from 'hooks/usePaths';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { AppContext } from 'App';

const TemplatesPage: React.FC = () => {
  const theme = useTheme() as Theme;
  const classes = useStyles(theme);
  const { categoryRepresentations, isTemplatesCategoriesLoading, onDownload } = useTemplatesPage();
  const { formatMessage } = useIntl();
  const location = useLocation();
  const paths = usePaths();
  const { setIsAccessRequestOpen } = useContext(AppContext);

  useEffect(() => {
    if (!isTemplatesCategoriesLoading) {
      let scrollTimeout: ReturnType<typeof setTimeout>;

      if (location.hash) {
        scrollTimeout = setTimeout(() => {
          document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
        }, 200);
      }

      return () => {
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
      };
    }
    // eslint-disable-next-line
  }, [isTemplatesCategoriesLoading, location]);

  return (
    <CommonPageLayout>
      <Helmet>
        <title>{formatMessage({ id: 'pageTitle.templates', defaultMessage: 'Templates' })}</title>
      </Helmet>
      <Box p={8}>
        <Box mb={7}>
          <Proposals />
        </Box>
        <Heading>
          <FormattedMessage id={'TemplatesPage.templates'} defaultMessage={'Templates'} />
        </Heading>
        <Box css={classes.items}>
          {isTemplatesCategoriesLoading ? (
            <Box mb={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {Object.entries(categoryRepresentations ?? {}).map(([categoryId, categoryItem], index) => {
                return !categoryItem.isFakeCategory ? (
                  <Box key={categoryId} mb={10}>
                    <Box
                      css={classes.categoryTitle}
                      style={{ backgroundColor: categoryItem?.bg ?? 'lightgrey' }}
                      mb={4}
                      p={2}
                      id={categoryId}
                    >
                      <span css={classes.num} style={{ color: categoryItem?.numColor ?? 'inherit' }}>
                        {index + 1}
                      </span>
                      <Typography
                        component="h2"
                        variant="subtitle1"
                        style={{ color: categoryItem?.color ?? 'inherit' }}
                      >
                        {categoryItem.name}
                      </Typography>
                      <span css={classes.categoryArrow} style={{ color: categoryItem?.bg ?? 'lightgrey' }}></span>
                    </Box>
                    <div css={classes.list}>
                      {categoryItem.templates.map(item => (
                        <TemplatesItem key={item.id} item={item} onDownload={onDownload} />
                      ))}
                    </div>
                  </Box>
                ) : null;
              })}
              <Box mb={10}>
                <Box css={[classes.categoryTitle, classes.autoCategoryTitle]} mb={4} p={2} id="inpp">
                  <span css={[classes.num, classes.autoCategoryNum]}>
                    <TabGroupIcon />
                  </span>
                  <Typography component="h2" variant="subtitle1">
                    {categoryRepresentations?.['inpp']?.name}
                  </Typography>
                </Box>
                <Typography component="p" variant="body1">
                  Completing the optional templates which are available and described on this page is intended to
                  support users in the development of national plans and proposals, including for the Pandemic Fund.
                  Uploading the completed templates at the following link will also enable the generation of national
                  investment plans and proposals:{' '}
                  <AuthenticatedTemplate>
                    <MaterialLink component={Link} to={paths.uploads.generate()}>
                      Uploads
                    </MaterialLink>
                  </AuthenticatedTemplate>
                  <UnauthenticatedTemplate>
                    <MaterialLink css={classes.uploadsLink} onClick={() => setIsAccessRequestOpen?.(true)}>
                      Uploads
                    </MaterialLink>
                  </UnauthenticatedTemplate>
                  . All templates and data uploaded will be stored and shared in line with the selected data sharing
                  option.
                </Typography>
                {false && (
                  <Box css={classes.list} mt={4}>
                    {categoryRepresentations?.['inpp']?.templates?.map(item => (
                      <TemplatesItem key={item.id} item={item} onDownload={onDownload} />
                    ))}
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </CommonPageLayout>
  );
};

const useStyles = (theme: Theme): UseStyles => ({
  items: {},
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  categoryTitle: {
    display: 'inline-flex',
    alignItems: 'center',
    fontWeight: 500,
    height: 56,
    borderRadius: '28px 0 0 28px',
    gap: theme.spacing(2),
    position: 'relative',
  },
  num: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    flexShrink: 0,
    fontWeight: 600,
    fontSize: theme.typography.custom.text.md.fontSize,
    borderRadius: '50%',
    background: theme.palette.custom.primary.grey[50],
  },
  categoryArrow: {
    display: 'block',
    width: 0,
    height: 0,
    position: 'absolute',
    right: -17,
    top: 0,
    borderStyle: 'solid',
    borderColor: 'transparent currentColor',
    borderWidth: '28px 0 28px 17px',

    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      left: -18,
      top: -27,
      height: 54,
      width: 2,
      background: 'currentColor',
    },
  },
  autoCategoryTitle: {
    display: 'flex',
    background: theme.palette.custom.secondary.purple[120],
    borderRadius: 28,
    color: theme.palette.common.white,
  },
  autoCategoryNum: {
    background: 'none',
  },
  uploadsLink: {
    cursor: 'pointer',
  },
});

export default TemplatesPage;
